import { useCallback, useEffect, useState } from "react";
import { Badge, type BadgeTone } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { Dialog } from "@/components/ui/Dialog";
import { EmptyState } from "@/components/ui/EmptyState";
import { Field } from "@/components/ui/Field";
import { Input, Textarea } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Table, Td, Th } from "@/components/ui/Table";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/useToast";
import { formatDateTime } from "@/lib/format";
import { analysis, type AnalysisRun } from "@/lib/analysis";
import { publications, type Publication, type PublicationDetail } from "@/lib/phase2";
import { strings } from "@/lib/strings";

const STATUS_TONE: Record<Publication["status"], BadgeTone> = {
  draft: "info",
  approved: "warning",
  published: "ok",
  withdrawn: "neutral",
};

/**
 * Proposing, approving and publishing the antibiogram (PB-08 … PB-11).
 *
 * Two people, not one: the Data Manager proposes and the microbiologist
 * approves, and never the same person for both. A clinical figure going in
 * front of the public should have been looked at twice.
 */
export function PublicationsAdmin() {
  const { user, hasRole } = useAuth();
  const { notify } = useToast();
  const canApprove = hasRole("microbiologist", "administrator");

  const [items, setItems] = useState<Publication[]>([]);
  const [runs, setRuns] = useState<AnalysisRun[]>([]);
  const [detail, setDetail] = useState<PublicationDetail | null>(null);
  const [proposing, setProposing] = useState(false);
  const [withdrawing, setWithdrawing] = useState<Publication | null>(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const [form, setForm] = useState({
    analysis_run_id: "",
    title: "",
    period_label: "",
    notes: "",
    percent_threshold: 30,
    count_threshold: 5,
  });

  const load = useCallback(async () => {
    try {
      const [list, runList] = await Promise.all([
        publications.list(),
        analysis.listRuns(),
      ]);
      setItems(list);
      setRuns(runList);
    } catch {
      notify(strings.errors.forbidden, "error");
    }
  }, [notify]);

  useEffect(() => {
    void load();
  }, [load]);

  const act = async (label: string, fn: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await fn();
      notify(label, "success");
      await load();
    } catch (error) {
      notify(error instanceof Error ? error.message : strings.errors.generic, "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Public antibiogram</h1>
          <p className="text-sm text-slate-500">
            What the hospital publishes outside the platform.
          </p>
        </div>
        <Button onClick={() => setProposing(true)} disabled={runs.length === 0}>
          Propose a report
        </Button>
      </div>

      <Card>
        <CardBody>
          <p className="text-sm text-slate-700">
            A published report contains aggregated counts only — no isolate, no
            hospital number, no date finer than a month. Percentages are hidden
            where too few isolates were tested, and an organism with very few
            isolates is left out entirely, because in one hospital in one year
            that can identify a patient.
          </p>
          <p className="mt-2 text-sm text-slate-700">
            The public site is a set of static files on its own address with no
            connection to this database.
          </p>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Reports" />
        {items.length === 0 ? (
          <EmptyState title="Nothing proposed yet." />
        ) : (
          <Table caption="Proposed and published antibiograms">
            <thead>
              <tr>
                <Th>Title</Th>
                <Th>Period</Th>
                <Th>Status</Th>
                <Th>Proposed</Th>
                <Th>
                  <span className="sr-only">Actions</span>
                </Th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} className="hover:bg-slate-50">
                  <Td>
                    {item.title}
                    {item.is_current && (
                      <Badge tone="brand" className="ml-2">
                        live
                      </Badge>
                    )}
                  </Td>
                  <Td>{item.period_label || item.as_of_month}</Td>
                  <Td>
                    <Badge tone={STATUS_TONE[item.status]}>{item.status}</Badge>
                  </Td>
                  <Td className="whitespace-nowrap text-xs">
                    {formatDateTime(item.created_at)}
                  </Td>
                  <Td className="whitespace-nowrap">
                    <div className="flex flex-wrap gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() =>
                          void publications.get(item.id).then(setDetail).catch(() => undefined)
                        }
                      >
                        Review
                      </Button>

                      {item.status === "draft" && canApprove && (
                        <Button
                          size="sm"
                          variant="secondary"
                          loading={busy}
                          // PB-09: the proposer cannot approve their own report.
                          disabled={item.proposed_by === user?.id}
                          title={
                            item.proposed_by === user?.id
                              ? "You proposed this, so a colleague must approve it"
                              : undefined
                          }
                          onClick={() =>
                            void act("Approved.", () => publications.approve(item.id))
                          }
                        >
                          Approve
                        </Button>
                      )}

                      {item.status === "approved" && canApprove && (
                        <Button
                          size="sm"
                          loading={busy}
                          onClick={() =>
                            void act("Published.", () => publications.publish(item.id))
                          }
                        >
                          Publish
                        </Button>
                      )}

                      {item.status === "published" && canApprove && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setWithdrawing(item)}
                        >
                          Withdraw
                        </Button>
                      )}
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      {/* ---- propose ---- */}
      <Dialog
        open={proposing}
        onOpenChange={setProposing}
        title="Propose a report for publication"
        description="Built from an antibiogram you have already produced, then redacted."
        footer={
          <>
            <Button variant="secondary" onClick={() => setProposing(false)}>
              {strings.common.cancel}
            </Button>
            <Button
              loading={busy}
              disabled={!form.analysis_run_id || form.title.trim().length < 3}
              onClick={() =>
                void act("Proposed. A microbiologist must approve it.", async () => {
                  await publications.propose(form);
                  setProposing(false);
                })
              }
            >
              Propose
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Field id="run" label="Which antibiogram?" required>
            <Select
              id="run"
              placeholder="Choose one"
              value={form.analysis_run_id}
              onChange={(e) => setForm({ ...form, analysis_run_id: e.target.value })}
              options={runs.map((run) => ({
                value: run.id,
                label: `${run.title || "untitled"} — ${run.isolates_included} isolates, ${formatDateTime(run.created_at)}`,
              }))}
            />
          </Field>
          <Field id="pub-title" label="Title" required>
            <Input
              id="pub-title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="EKSUTH cumulative antibiogram 2026"
            />
          </Field>
          <Field id="pub-period" label="Period">
            <Input
              id="pub-period"
              value={form.period_label}
              onChange={(e) => setForm({ ...form, period_label: e.target.value })}
              placeholder="January to December 2026"
            />
          </Field>
          <Field
            id="pub-notes"
            label="Notes for readers"
            hint="Caveats worth stating, for instance about de-duplication coverage."
          >
            <Textarea
              id="pub-notes"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field
              id="pct"
              label="Hide % below n"
              hint="CLSI M39 recommends 30."
            >
              <Input
                id="pct"
                type="number"
                min={0}
                value={form.percent_threshold}
                onChange={(e) =>
                  setForm({ ...form, percent_threshold: Number(e.target.value || 0) })
                }
              />
            </Field>
            <Field
              id="cnt"
              label="Withhold entirely below n"
              hint="A count of one or two is a person."
            >
              <Input
                id="cnt"
                type="number"
                min={1}
                value={form.count_threshold}
                onChange={(e) =>
                  setForm({ ...form, count_threshold: Number(e.target.value || 1) })
                }
              />
            </Field>
          </div>
        </div>
      </Dialog>

      {/* ---- review ---- */}
      <Dialog
        open={detail !== null}
        onOpenChange={() => setDetail(null)}
        title={detail?.title ?? "Report"}
        description="Exactly what would be published. Nothing else is sent."
      >
        {detail && (
          <div className="space-y-3 text-sm">
            <dl className="grid grid-cols-2 gap-x-4 gap-y-1">
              <dt className="text-slate-500">Status</dt>
              <dd>{detail.status}</dd>
              <dt className="text-slate-500">Data as of</dt>
              <dd>{detail.as_of_month}</dd>
              <dt className="text-slate-500">Organisms shown</dt>
              <dd>{(detail.payload.organisms as string[] | undefined)?.length ?? 0}</dd>
              <dt className="text-slate-500">Isolates</dt>
              <dd>{String(detail.payload.total_isolates ?? 0)}</dd>
            </dl>
            {detail.withdrawn_reason && (
              <p className="rounded-md bg-slate-50 p-3">
                Withdrawn: {detail.withdrawn_reason}
              </p>
            )}
            <details>
              <summary className="cursor-pointer text-brand-700 underline">
                Show the full payload
              </summary>
              <pre className="mt-2 max-h-64 overflow-auto rounded bg-slate-50 p-2 text-xs">
                {JSON.stringify(detail.payload, null, 2)}
              </pre>
            </details>
          </div>
        )}
      </Dialog>

      {/* ---- withdraw ---- */}
      <Dialog
        open={withdrawing !== null}
        onOpenChange={() => setWithdrawing(null)}
        title="Withdraw this report"
        description="It stops being public. The record and your reason are kept."
        footer={
          <>
            <Button variant="secondary" onClick={() => setWithdrawing(null)}>
              {strings.common.cancel}
            </Button>
            <Button
              variant="danger"
              disabled={reason.trim().length < 3}
              onClick={() =>
                void act("Withdrawn.", async () => {
                  if (withdrawing) await publications.withdraw(withdrawing.id, reason.trim());
                  setWithdrawing(null);
                  setReason("");
                })
              }
            >
              Withdraw
            </Button>
          </>
        }
      >
        <Field id="withdraw-reason" label="Why?" required>
          <Textarea
            id="withdraw-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Ward coding error found after publication."
          />
        </Field>
      </Dialog>
    </div>
  );
}
